import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Button,
  Typography,
  Container,
  Grid,
  Card,
  CardContent,
  Stack,
  Fade,
  useTheme,
  Chip,
  Divider,
} from "@mui/material";
import {
  ArrowForward,
  AutoAwesome,
  Security,
  // Verified,
  Article,
  Public,
  Email,
  Storage,
  Cloud,
  Groups,
  Link as LinkIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import * as d3 from "d3";
import type { Theme } from "@mui/material/styles";

interface GalaxyNode extends d3.SimulationNodeDatum {
  id: string;
  name: string;
  type: "contact" | "social" | "platform";
  size: number;
  color: string;
  x?: number;
  y?: number;
}

interface GalaxyLink extends d3.SimulationLinkDatum<GalaxyNode> {
  source: string | GalaxyNode;
  target: string | GalaxyNode;
}

interface PulsingNode extends d3.SimulationNodeDatum {
  id: string;
  color: string;
  size: number;
  pulsePhase: number;
  x?: number;
  y?: number;
}

interface PulsingLink extends d3.SimulationLinkDatum<PulsingNode> {
  source: string | PulsingNode;
  target: string | PulsingNode;
  type: "solid" | "dashed" | "dotted";
}

const PulsingGraphBackground: React.FC<{ theme: Theme }> = ({ theme }) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const animationFrameRef = useRef<number>();
  const pulseTimeRef = useRef(0);
  const simulationRef = useRef<d3.Simulation<PulsingNode, PulsingLink> | null>(
    null
  );

  useEffect(() => {
    if (!svgRef.current) return;

    const container = svgRef.current.parentElement;
    if (!container) return;

    const updateSize = () => {
      return container.offsetWidth || window.innerWidth;
    };

    const width = updateSize();
    const height = 600;
    const isDark = theme.palette.mode === "dark";

    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();
    svg.attr("width", width).attr("height", height);

    // Create nodes with different colors
    const nodeColors = [
      "#1976d2",
      "#42a5f5",
      "#9c27b0",
      "#ba68c8",
      "#2e7d32",
      "#66bb6a",
      "#f57c00",
      "#ff9800",
      "#d32f2f",
      "#ef5350",
      "#0288d1",
      "#29b6f6",
    ];

    const nodes: PulsingNode[] = Array.from({ length: 25 }, (_, i) => ({
      id: `node-${i}`,
      color: nodeColors[i % nodeColors.length],
      size: 8 + Math.random() * 12,
      pulsePhase: Math.random() * Math.PI * 2,
    }));

    // Create links with different line types
    const links: PulsingLink[] = [];
    for (let i = 0; i < nodes.length; i++) {
      // Each node connects to 2-4 other nodes
      const numConnections = 2 + Math.floor(Math.random() * 3);
      const connected = new Set<number>();
      connected.add(i);

      for (let j = 0; j < numConnections; j++) {
        let targetIndex;
        do {
          targetIndex = Math.floor(Math.random() * nodes.length);
        } while (connected.has(targetIndex));

        connected.add(targetIndex);

        // Assign line type based on connection index
        const lineTypes: ("solid" | "dashed" | "dotted")[] = [
          "solid",
          "dashed",
          "dotted",
        ];
        const lineType = lineTypes[j % lineTypes.length];

        links.push({
          source: nodes[i].id,
          target: nodes[targetIndex].id,
          type: lineType,
        });
      }
    }

    // Create force simulation
    const simulation = d3
      .forceSimulation(nodes)
      .force(
        "link",
        d3
          .forceLink(links)
          .id((d: any) => d.id)
          .distance(120)
      )
      .force("charge", d3.forceManyBody().strength(-400))
      .force("center", d3.forceCenter(width / 2, height / 2))
      .force(
        "collision",
        d3.forceCollide().radius((d: any) => d.size + 8)
      );

    simulationRef.current = simulation;

    // Create links with different stroke dash arrays
    const link = svg
      .append("g")
      .selectAll("line")
      .data(links)
      .enter()
      .append("line")
      .attr("stroke", (d) => {
        const sourceId =
          typeof d.source === "string"
            ? d.source
            : (d.source as PulsingNode).id;
        const sourceNode = nodes.find((n) => n.id === sourceId);
        if (sourceNode) {
          const r = parseInt(sourceNode.color.slice(1, 3), 16);
          const g = parseInt(sourceNode.color.slice(3, 5), 16);
          const b = parseInt(sourceNode.color.slice(5, 7), 16);
          return isDark
            ? `rgba(${r}, ${g}, ${b}, 0.3)`
            : `rgba(${r}, ${g}, ${b}, 0.4)`;
        }
        return isDark ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.2)";
      })
      .attr("stroke-width", 1.5)
      .attr("stroke-dasharray", (d) => {
        if (d.type === "dashed") return "5,5";
        if (d.type === "dotted") return "2,3";
        return "0"; // solid
      });

    // Create nodes
    const node = svg
      .append("g")
      .selectAll("circle")
      .data(nodes)
      .enter()
      .append("circle")
      .attr("r", (d) => d.size)
      .attr("fill", (d) => d.color)
      .attr("opacity", 0.8)
      .style("filter", (d) => `drop-shadow(0 0 ${d.size}px ${d.color}80)`);

    // Animation loop for pulsing
    const animate = () => {
      pulseTimeRef.current += 0.02;

      node.each(function (d) {
        const pulse = Math.sin(pulseTimeRef.current + d.pulsePhase) * 0.3 + 1;
        const currentSize = d.size * pulse;
        d3.select(this)
          .attr("r", currentSize)
          .attr("opacity", 0.6 + pulse * 0.4);
      });

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    // Update positions on simulation tick
    simulation.on("tick", () => {
      link
        .attr("x1", (d: any) => {
          const source =
            typeof d.source === "string"
              ? nodes.find((n) => n.id === d.source)
              : d.source;
          return source?.x || 0;
        })
        .attr("y1", (d: any) => {
          const source =
            typeof d.source === "string"
              ? nodes.find((n) => n.id === d.source)
              : d.source;
          return source?.y || 0;
        })
        .attr("x2", (d: any) => {
          const target =
            typeof d.target === "string"
              ? nodes.find((n) => n.id === d.target)
              : d.target;
          return target?.x || 0;
        })
        .attr("y2", (d: any) => {
          const target =
            typeof d.target === "string"
              ? nodes.find((n) => n.id === d.target)
              : d.target;
          return target?.y || 0;
        });

      node.attr("cx", (d: any) => d.x || 0).attr("cy", (d: any) => d.y || 0);
    });

    // Start animation
    animate();

    // Handle window resize
    const handleResize = () => {
      if (simulationRef.current && svgRef.current) {
        const newWidth = updateSize();
        simulationRef.current.force(
          "center",
          d3.forceCenter(newWidth / 2, height / 2)
        );
        simulationRef.current.alpha(0.3).restart();
      }
    };

    window.addEventListener("resize", handleResize);

    // Cleanup
    return () => {
      window.removeEventListener("resize", handleResize);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (simulationRef.current) {
        simulationRef.current.stop();
      }
    };
  }, [theme.palette.mode]);

  return (
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: "600px",
        zIndex: 0,
        overflow: "hidden",
        pointerEvents: "none",
        opacity: 0.4,
        mixBlendMode: "normal",
      }}
    >
      <svg
        ref={svgRef}
        style={{
          display: "block",
          width: "100%",
          height: "100%",
        }}
      />
    </Box>
  );
};

const GalaxySample: React.FC<{ theme: Theme }> = ({ theme }) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!svgRef.current || !containerRef.current) return;

    const width = Math.min(1200, containerRef.current.offsetWidth);
    const height = 600;
    const isDark = theme.palette.mode === "dark";

    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();
    svg.attr("width", width).attr("height", height);

    // Create sample nodes
    const nodes: GalaxyNode[] = [
      // Social media platforms (even smaller, multiple unique instances per contact)
      // Sarah Chen's social platforms
      {
        id: "linkedin1",
        name: "LinkedIn",
        type: "social",
        size: 14,
        color: "#0077b5",
      },
      { id: "twitter1", name: "X", type: "social", size: 14, color: "#000000" },

      // Marcus R.'s social platforms
      {
        id: "linkedin2",
        name: "LinkedIn",
        type: "social",
        size: 14,
        color: "#0077b5",
      },
      { id: "twitter2", name: "X", type: "social", size: 14, color: "#000000" },
      {
        id: "facebook1",
        name: "Facebook",
        type: "social",
        size: 14,
        color: "#1877F2",
      },

      // Priya P.'s social platforms
      {
        id: "instagram1",
        name: "Instagram",
        type: "social",
        size: 14,
        color: "#E4405F",
      },
      {
        id: "linkedin3",
        name: "LinkedIn",
        type: "social",
        size: 14,
        color: "#0077b5",
      },

      // David K.'s social platforms
      { id: "twitter3", name: "X", type: "social", size: 14, color: "#000000" },

      // Emma T.'s social platforms
      {
        id: "instagram2",
        name: "Instagram",
        type: "social",
        size: 14,
        color: "#E4405F",
      },
      {
        id: "linkedin4",
        name: "LinkedIn",
        type: "social",
        size: 14,
        color: "#0077b5",
      },

      // James W.'s social platforms
      { id: "twitter4", name: "X", type: "social", size: 14, color: "#000000" },
      {
        id: "facebook2",
        name: "Facebook",
        type: "social",
        size: 14,
        color: "#1877F2",
      },

      // Contacts
      {
        id: "contact1",
        name: "Sarah C.",
        type: "contact",
        size: 25,
        color: isDark ? "#42a5f5" : "#1976d2",
      },
      {
        id: "contact2",
        name: "Marcus R.",
        type: "contact",
        size: 25,
        color: isDark ? "#42a5f5" : "#1976d2",
      },
      {
        id: "contact3",
        name: "Priya P.",
        type: "contact",
        size: 25,
        color: isDark ? "#42a5f5" : "#1976d2",
      },
      {
        id: "contact4",
        name: "David K.",
        type: "contact",
        size: 25,
        color: isDark ? "#42a5f5" : "#1976d2",
      },
      {
        id: "contact5",
        name: "Emma T.",
        type: "contact",
        size: 25,
        color: isDark ? "#42a5f5" : "#1976d2",
      },
      {
        id: "contact6",
        name: "James W.",
        type: "contact",
        size: 25,
        color: isDark ? "#42a5f5" : "#1976d2",
      },
    ];

    // Create links - multiple unique social nodes per contact
    const links: GalaxyLink[] = [
      // Sarah Chen's connections
      { source: "contact1", target: "linkedin1" },
      { source: "contact1", target: "twitter1" },

      // Marcus R.'s connections
      { source: "contact2", target: "linkedin2" },
      { source: "contact2", target: "twitter2" },
      { source: "contact2", target: "facebook1" },

      // Priya P.'s connections
      { source: "contact3", target: "instagram1" },
      { source: "contact3", target: "linkedin3" },

      // David K.'s connections
      { source: "contact4", target: "twitter3" },

      // Emma T.'s connections
      { source: "contact5", target: "instagram2" },
      { source: "contact5", target: "linkedin4" },

      // James W.'s connections
      { source: "contact6", target: "twitter4" },
      { source: "contact6", target: "facebook2" },

      // Contact-to-contact connections (multiple connections for richer network)
      { source: "contact1", target: "contact2" },
      { source: "contact1", target: "contact3" },
      { source: "contact1", target: "contact4" },
      { source: "contact2", target: "contact3" },
      { source: "contact2", target: "contact5" },
      { source: "contact3", target: "contact4" },
      { source: "contact3", target: "contact6" },
      { source: "contact4", target: "contact5" },
      { source: "contact4", target: "contact6" },
      { source: "contact5", target: "contact6" },
    ];

    // Create force simulation
    const simulation = d3
      .forceSimulation(nodes)
      .force(
        "link",
        d3
          .forceLink(links)
          .id((d: any) => d.id)
          .distance(100)
      )
      .force("charge", d3.forceManyBody().strength(-300))
      .force("center", d3.forceCenter(width / 2, height / 2))
      .force(
        "collision",
        d3.forceCollide().radius((d: any) => d.size + 6)
      );

    // Create links
    const link = svg
      .append("g")
      .selectAll("line")
      .data(links)
      .enter()
      .append("line")
      .attr(
        "stroke",
        isDark ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.2)"
      )
      .attr("stroke-width", 1.5);

    // Create nodes
    const node = svg
      .append("g")
      .selectAll("g")
      .data(nodes)
      .enter()
      .append("g")
      .call(
        d3
          .drag<SVGGElement, GalaxyNode>()
          .on("start", (event, d) => {
            if (!event.active) simulation.alphaTarget(0.3).restart();
            d.fx = d.x;
            d.fy = d.y;
          })
          .on("drag", (event, d) => {
            d.fx = event.x;
            d.fy = event.y;
          })
          .on("end", (event, d) => {
            if (!event.active) simulation.alphaTarget(0);
            d.fx = null;
            d.fy = null;
          })
      );

    // Add circles for nodes
    node
      .append("circle")
      .attr("r", (d) => d.size)
      .attr("fill", (d) => d.color)
      .attr(
        "stroke",
        isDark ? "rgba(255, 255, 255, 0.3)" : "rgba(255, 255, 255, 0.8)"
      )
      .attr("stroke-width", 2)
      .style("filter", (d) => {
        if (d.type === "social") {
          return `drop-shadow(0 0 8px ${d.color}80)`;
        }
        return "drop-shadow(0 0 4px rgba(0, 0, 0, 0.2))";
      });

    // Add logos/text inside social nodes
    node
      .filter((d) => d.type === "social")
      .append("text")
      .text((d) => {
        // Return logo/initials for each platform
        if (d.name === "LinkedIn") return "in";
        if (d.name === "X") return "X";
        if (d.name === "Instagram") return "IG";
        if (d.name === "Facebook") return "f";
        return "";
      })
      .attr("text-anchor", "middle")
      .attr("dy", "0.35em")
      .attr("fill", "white")
      .style("font-size", "9px")
      .style("font-weight", "700")
      .style("pointer-events", "none")
      .style("user-select", "none");

    // Add labels (only for contacts, not social nodes)
    node
      .filter((d) => d.type === "contact")
      .append("text")
      .text((d) => d.name)
      .attr("text-anchor", "middle")
      .attr("dy", (d) => d.size + 15)
      .attr("fill", isDark ? "rgba(255, 255, 255, 0.9)" : "rgba(0, 0, 0, 0.8)")
      .style("font-size", "12px")
      .style("font-weight", "500")
      .style("pointer-events", "none");

    // Update positions on simulation tick
    simulation.on("tick", () => {
      link
        .attr("x1", (d: any) => d.source.x)
        .attr("y1", (d: any) => d.source.y)
        .attr("x2", (d: any) => d.target.x)
        .attr("y2", (d: any) => d.target.y);

      node.attr("transform", (d: any) => `translate(${d.x},${d.y})`);
    });

    // Cleanup
    return () => {
      simulation.stop();
    };
  }, [theme.palette.mode]);

  return (
    <Box
      ref={containerRef}
      sx={{
        width: "100%",
        display: "flex",
        justifyContent: "center",
        p: 3,
        borderRadius: 2,
        bgcolor:
          theme.palette.mode === "dark"
            ? "rgba(255, 255, 255, 0.03)"
            : "rgba(255, 255, 255, 0.8)",
        border: `1px solid ${
          theme.palette.mode === "dark"
            ? "rgba(255, 255, 255, 0.1)"
            : "rgba(0, 0, 0, 0.1)"
        }`,
        overflow: "hidden",
      }}
    >
      <svg ref={svgRef} style={{ display: "block" }} />
    </Box>
  );
};

export const Homepage: React.FC = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const theme = useTheme();
  const [fadeIn, setFadeIn] = useState(false);

  // Redirect logged-in users to dashboard
  useEffect(() => {
    if (!loading && user) {
      navigate("/dashboard");
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    setFadeIn(true);
  }, []);

  const handleGetStarted = () => {
    navigate("/register");
  };

  const handleLogin = () => {
    navigate("/login");
  };

  if (loading) {
    return (
      <Box
        sx={{
          width: "100vw",
          height: "100vh",
          bgcolor: "background.default",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Typography variant="h6">Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "background.default",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          borderBottom: 1,
          borderColor: "divider",
          backdropFilter: "blur(10px)",
          bgcolor:
            theme.palette.mode === "dark"
              ? "rgba(18, 18, 18, 0.9)"
              : "rgba(255, 255, 255, 0.9)",
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              py: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <img
                src="/praytell_logo.png"
                alt="MyCatalyze"
                style={{
                  height: 32,
                  width: "auto",
                  filter:
                    theme.palette.mode === "dark"
                      ? "brightness(0) invert(1)"
                      : "none",
                }}
              />
              <Typography variant="h6" component="h1" fontWeight="bold">
                MyCatalyze
              </Typography>
            </Box>

            <Stack direction="row" spacing={2}>
              {!user && (
                <Button
                  variant="text"
                  onClick={handleLogin}
                  sx={{
                    color: "text.primary",
                  }}
                >
                  Sign In
                </Button>
              )}
              <Button
                variant="contained"
                onClick={user ? () => navigate("/dashboard") : handleGetStarted}
                endIcon={<ArrowForward />}
              >
                {user ? "Dashboard" : "Get Started"}
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* Hero Section */}
      <Box sx={{ pt: 15, pb: 8, position: "relative", zIndex: 1 }}>
        {/* Pulsing Graph Background */}
        <PulsingGraphBackground theme={theme} />
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 2 }}>
          <Fade in={fadeIn} timeout={1000}>
            <Box
              sx={{
                textAlign: "center",
                mb: 10,
                position: "relative",
                zIndex: 2,
              }}
            >
              <Typography
                variant="h1"
                component="h1"
                sx={{
                  fontSize: {
                    xs: "2.5rem",
                    sm: "3.5rem",
                    md: "4.5rem",
                    lg: "5.5rem",
                  },
                  fontWeight: 900,
                  mb: 2,
                  background:
                    theme.palette.mode === "dark"
                      ? "linear-gradient(135deg, #ffffff 0%, #b0bec5 100%)"
                      : "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  lineHeight: 1.2,
                }}
              >
                Network Stewardship
              </Typography>

              <Typography
                variant="h4"
                component="h2"
                sx={{
                  fontSize: {
                    xs: "1.5rem",
                    sm: "2rem",
                    md: "2.5rem",
                    lg: "3rem",
                  },
                  fontWeight: 600,
                  mb: 3,
                  color: "text.secondary",
                  lineHeight: 1.3,
                }}
              >
                Private Intelligence for Your Career, Capital Raising, and Influence
              </Typography>

              <Typography
                variant="h5"
                component="p"
                sx={{
                  color: "text.secondary",
                  mb: 4,
                  maxWidth: "800px",
                  mx: "auto",
                  fontSize: { xs: "1.1rem", md: "1.5rem" },
                  lineHeight: 1.6,
                  fontWeight: 400,
                }}
              >
                We map your investors, stakeholders, and power relationships — then help you achieve the outcomes that depend on them.
              </Typography>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                justifyContent="center"
                sx={{ mb: 4 }}
              >
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleGetStarted}
                  endIcon={<ArrowForward />}
                  sx={{
                    px: 5,
                    py: 2,
                    fontSize: "1.1rem",
                    fontWeight: 700,
                    textTransform: "none",
                    boxShadow: 6,
                    "&:hover": {
                      boxShadow: 10,
                      transform: "translateY(-2px)",
                    },
                    transition: "all 0.2s ease-in-out",
                  }}
                >
                  Get started
                </Button>
              </Stack>
            </Box>
          </Fade>

          {/* Example Assistant Highlight */}
          <Fade in={fadeIn} timeout={1500}>
            <Box sx={{ mb: 12 }}>
              <Grid container spacing={6} alignItems="center">
                <Grid
                  item
                  xs={12}
                  md={4}
                  sx={{ textAlign: { xs: "center", md: "left" } }}
                >
                  <Box
                    component="img"
                    src="/team.png"
                    alt="Team"
                    sx={{
                      width: { xs: 240, md: 300 },
                      height: { xs: 240, md: 300 },
                      mx: { xs: "auto", md: 0 },
                      mb: 3,
                      boxShadow: `0 8px 24px ${theme.palette.primary.main}40`,
                      borderRadius: 0,
                      display: "block",
                    }}
                  />
                  {/* <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      justifyContent: { xs: "center", md: "flex-start" },
                      mb: 1,
                    }}
                  >
                    <Typography variant="h4" component="h3" fontWeight={800}>
                      Sandra H.
                    </Typography>
                    <Verified sx={{ fontSize: 28, color: "primary.main" }} />
                  </Box>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    Chief of Staff
                  </Typography>
                  <Chip
                    label="Available Now"
                    color="success"
                    size="small"
                    sx={{ fontWeight: 600 }}
                  /> */}
                </Grid>
                <Grid item xs={12} md={8}>
                  <Typography
                    variant="h5"
                    component="h4"
                    fontWeight={700}
                    sx={{ mb: 2 }}
                  >
                    Meet Your Chief of Staff Intelligence System
                  </Typography>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ mb: 3, lineHeight: 1.8 }}
                  >
                    Catalyze's AI-powered assistants serve as your Chief of
                    Staff. They provide executive-level coordination and
                    comprehensive intelligence reports. Combined with Catalyze's
                    platform, your assistant monitors news, social media, and
                    your personal data sources to keep you informed.
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={2}
                    flexWrap="wrap"
                    sx={{ gap: 1, mb: 3 }}
                  >
                    <Chip
                      icon={<Groups />}
                      label="Investor & Stakeholder Mapping"
                      variant="outlined"
                      sx={{ fontWeight: 500 }}
                    />
                    <Chip
                      icon={<AutoAwesome />}
                      label="Personalized Influence Strategy"
                      variant="outlined"
                      sx={{ fontWeight: 500 }}
                    />
                    <Chip
                      icon={<Security />}
                      label="Secure, Private, Individual Deployment"
                      variant="outlined"
                      sx={{ fontWeight: 500 }}
                    />
                    <Chip
                      icon={<Article />}
                      label="Context-Aware Recommendations"
                      variant="outlined"
                      sx={{ fontWeight: 500 }}
                    />
                  </Stack>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleGetStarted}
                    endIcon={<ArrowForward />}
                    sx={{
                      px: 4,
                      py: 1.5,
                      fontSize: "1rem",
                      fontWeight: 700,
                      textTransform: "none",
                      boxShadow: 4,
                      "&:hover": {
                        boxShadow: 6,
                        transform: "translateY(-2px)",
                      },
                      transition: "all 0.2s ease-in-out",
                    }}
                  >
                    Build Your Network Strategy
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Fade>

          {/* Subtle Divider */}
          <Box sx={{ mb: 8, display: "flex", justifyContent: "center" }}>
            <Divider
              sx={{
                width: "100px",
                borderColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.12)"
                    : "rgba(0, 0, 0, 0.12)",
                borderWidth: 1,
              }}
            />
          </Box>

          {/* Three Services Section */}
          <Fade in={fadeIn} timeout={1800}>
            <Box sx={{ mb: 12 }}>
              <Box sx={{ textAlign: "center", mb: 6 }}>
                <Typography
                  variant="h3"
                  component="h2"
                  sx={{
                    mb: 2,
                    fontWeight: 700,
                  }}
                >
                  Three Services We Deliver
                </Typography>
                <Typography
                  variant="h6"
                  component="p"
                  sx={{
                    color: "text.secondary",
                    maxWidth: "800px",
                    mx: "auto",
                    fontSize: "1.1rem",
                    lineHeight: 1.7,
                  }}
                >
                  These are the core outcomes we help high-stakes individuals achieve.
                </Typography>
              </Box>

              <Grid container spacing={4}>
                {/* Service 1: Investor & Stakeholder Mapping */}
                <Grid item xs={12} md={4}>
                  <Card
                    sx={{
                      height: "100%",
                      p: 0,
                      border: `1px solid ${
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.12)"
                          : "rgba(0, 0, 0, 0.12)"
                      }`,
                      background:
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.05)"
                          : "rgba(255, 255, 255, 0.8)",
                      boxShadow:
                        theme.palette.mode === "dark"
                          ? "0 2px 8px rgba(0, 0, 0, 0.3)"
                          : "0 2px 8px rgba(0, 0, 0, 0.08)",
                      transition: "all 0.3s ease-in-out",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow:
                          theme.palette.mode === "dark"
                            ? "0 8px 24px rgba(0, 0, 0, 0.4)"
                            : "0 8px 24px rgba(0, 0, 0, 0.12)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        height: 4,
                        bgcolor: "#1976d2",
                        borderRadius: "4px 4px 0 0",
                      }}
                    />
                    <CardContent sx={{ p: 4 }}>
                      <Typography
                        variant="h4"
                        component="h3"
                        fontWeight={700}
                        sx={{ mb: 1 }}
                      >
                        1. Investor & Stakeholder Mapping
                      </Typography>
                      <Typography
                        variant="h6"
                        component="h4"
                        color="text.secondary"
                        sx={{ mb: 2, fontWeight: 500 }}
                      >
                        Understand exactly who matters — and why.
                      </Typography>
                      <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{ mb: 3, lineHeight: 1.8 }}
                      >
                        We map your LPs, prospective investors, board members, advisors, internal champions, blockers, and anyone influencing your trajectory.
                        Catalyze reveals hidden paths of influence, relationship strength, gaps, and opportunities.
                      </Typography>
                      <Box sx={{ mb: 2 }}>
                        <Typography
                          variant="subtitle2"
                          fontWeight={700}
                          sx={{ mb: 1 }}
                        >
                          Ideal for:
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                          • Fund managers raising their next vehicle
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                          • Emerging managers building credibility
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          • Executives navigating corporate politics
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: 1,
                          bgcolor:
                            theme.palette.mode === "dark"
                              ? "rgba(25, 118, 210, 0.1)"
                              : "rgba(25, 118, 210, 0.05)",
                          borderLeft: `3px solid #1976d2`,
                        }}
                      >
                        <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                          Outcome:
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          You know precisely whom to engage, how to frame your story, and where warm paths already exist.
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Service 2: Influence & Opportunity Positioning */}
                <Grid item xs={12} md={4}>
                  <Card
                    sx={{
                      height: "100%",
                      p: 0,
                      border: `1px solid ${
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.12)"
                          : "rgba(0, 0, 0, 0.12)"
                      }`,
                      background:
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.05)"
                          : "rgba(255, 255, 255, 0.8)",
                      boxShadow:
                        theme.palette.mode === "dark"
                          ? "0 2px 8px rgba(0, 0, 0, 0.3)"
                          : "0 2px 8px rgba(0, 0, 0, 0.08)",
                      transition: "all 0.3s ease-in-out",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow:
                          theme.palette.mode === "dark"
                            ? "0 8px 24px rgba(0, 0, 0, 0.4)"
                            : "0 8px 24px rgba(0, 0, 0, 0.12)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        height: 4,
                        bgcolor: "#9c27b0",
                        borderRadius: "4px 4px 0 0",
                      }}
                    />
                    <CardContent sx={{ p: 4 }}>
                      <Typography
                        variant="h4"
                        component="h3"
                        fontWeight={700}
                        sx={{ mb: 1 }}
                      >
                        2. Influence & Opportunity Positioning
                      </Typography>
                      <Typography
                        variant="h6"
                        component="h4"
                        color="text.secondary"
                        sx={{ mb: 2, fontWeight: 500 }}
                      >
                        Turn your network into a career engine.
                      </Typography>
                      <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{ mb: 3, lineHeight: 1.8 }}
                      >
                        Catalyze continuously analyzes your communication history, internal interactions, investors, mentors, and decision makers — and tells you:
                      </Typography>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                          • Who you're over-indexing on
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                          • Who you're neglecting
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                          • Who is rising in influence
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                          • Who talks about you and how
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          • What you should do this week to increase your leverage
                        </Typography>
                        <Typography
                          variant="subtitle2"
                          fontWeight={700}
                          sx={{ mb: 1 }}
                        >
                          Ideal for:
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                          • Executives seeking promotion
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                          • Leaders preparing for board or compensation cycles
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          • Managers who need visibility and influence
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: 1,
                          bgcolor:
                            theme.palette.mode === "dark"
                              ? "rgba(156, 39, 176, 0.1)"
                              : "rgba(156, 39, 176, 0.05)",
                          borderLeft: `3px solid #9c27b0`,
                        }}
                      >
                        <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                          Outcome:
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          A strategic plan to raise your profile, build internal support, and engineer opportunities before they appear.
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Service 3: Strategic Briefings & Action Plans */}
                <Grid item xs={12} md={4}>
                  <Card
                    sx={{
                      height: "100%",
                      p: 0,
                      border: `1px solid ${
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.12)"
                          : "rgba(0, 0, 0, 0.12)"
                      }`,
                      background:
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.05)"
                          : "rgba(255, 255, 255, 0.8)",
                      boxShadow:
                        theme.palette.mode === "dark"
                          ? "0 2px 8px rgba(0, 0, 0, 0.3)"
                          : "0 2px 8px rgba(0, 0, 0, 0.08)",
                      transition: "all 0.3s ease-in-out",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow:
                          theme.palette.mode === "dark"
                            ? "0 8px 24px rgba(0, 0, 0, 0.4)"
                            : "0 8px 24px rgba(0, 0, 0, 0.12)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        height: 4,
                        bgcolor: "#2e7d32",
                        borderRadius: "4px 4px 0 0",
                      }}
                    />
                    <CardContent sx={{ p: 4 }}>
                      <Typography
                        variant="h4"
                        component="h3"
                        fontWeight={700}
                        sx={{ mb: 1 }}
                      >
                        3. Strategic Briefings & Action Plans
                      </Typography>
                      <Typography
                        variant="h6"
                        component="h4"
                        color="text.secondary"
                        sx={{ mb: 2, fontWeight: 500 }}
                      >
                        You get a private weekly intelligence report designed for one person: you.
                      </Typography>
                      <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{ mb: 2, lineHeight: 1.8 }}
                      >
                        Your Chief of Staff produces continuous insights:
                      </Typography>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                          • A prioritized list of people to contact
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                          • Messaging suggestions
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                          • Investor or stakeholder updates
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                          • Risk alerts
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                          • Opportunities emerging in your network
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          • Narrative positioning for upcoming meetings
                        </Typography>
                        <Typography
                          variant="subtitle2"
                          fontWeight={700}
                          sx={{ mb: 1 }}
                        >
                          Ideal for:
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                          • Fund managers during a raise
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                          • Executives in politically complex orgs
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          • Individuals who want elite-level situational awareness
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: 1,
                          bgcolor:
                            theme.palette.mode === "dark"
                              ? "rgba(46, 125, 50, 0.1)"
                              : "rgba(46, 125, 50, 0.05)",
                          borderLeft: `3px solid #2e7d32`,
                        }}
                      >
                        <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                          Outcome:
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          You move through your professional world with clarity, confidence, and strategic advantage.
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          </Fade>

          {/* Subtle Divider */}
          <Box sx={{ mb: 8, display: "flex", justifyContent: "center" }}>
            <Divider
              sx={{
                width: "100px",
                borderColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.12)"
                    : "rgba(0, 0, 0, 0.12)",
                borderWidth: 1,
              }}
            />
          </Box>

          {/* Galaxy Visualization Sample */}
          <Fade in={fadeIn} timeout={2000}>
            <Box sx={{ mb: 12 }}>
              <Box sx={{ textAlign: "center", mb: 6 }}>
                <Typography
                  variant="h3"
                  component="h2"
                  sx={{
                    mb: 2,
                    fontWeight: 700,
                  }}
                >
                  Visualize Your Network
                </Typography>
                <Typography
                  variant="h6"
                  component="p"
                  sx={{
                    color: "text.secondary",
                    maxWidth: "800px",
                    mx: "auto",
                    fontSize: "1.1rem",
                    lineHeight: 1.7,
                  }}
                >
                  Catalyze transforms your emails, meetings, intros, and personal notes into a living map of your professional world.
                  You'll see your investor ecosystem, corporate power grid, allies, detractors, and hidden influencers — all in a single interactive view.
                </Typography>
              </Box>
              <GalaxySample theme={theme} />
              <Box sx={{ textAlign: "center", mt: 4 }}>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate("/galaxy")}
                  endIcon={<ArrowForward />}
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontSize: "1rem",
                    fontWeight: 700,
                    textTransform: "none",
                    borderWidth: 2,
                    "&:hover": {
                      borderWidth: 2,
                      transform: "translateY(-2px)",
                      boxShadow: 4,
                    },
                    transition: "all 0.2s ease-in-out",
                  }}
                >
                  View Your Influence Graph
                </Button>
              </Box>
            </Box>
          </Fade>

          {/* Subtle Divider */}
          <Box sx={{ mb: 8, display: "flex", justifyContent: "center" }}>
            <Divider
              sx={{
                width: "100px",
                borderColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.12)"
                    : "rgba(0, 0, 0, 0.12)",
                borderWidth: 1,
              }}
            />
          </Box>

          {/* Expert Network & Data Integrations */}
          <Fade in={fadeIn} timeout={2200}>
            <Box sx={{ mb: 12 }}>
              <Box sx={{ textAlign: "center", mb: 6 }}>
                <Typography
                  variant="h3"
                  component="h2"
                  sx={{
                    mb: 2,
                    fontWeight: 700,
                  }}
                >
                  Strategic Intelligence
                </Typography>
                <Typography
                  variant="h6"
                  component="p"
                  sx={{
                    color: "text.secondary",
                    maxWidth: "800px",
                    mx: "auto",
                    fontSize: "1.1rem",
                    lineHeight: 1.7,
                  }}
                >
                  Catalyze works with your data sources to build the most complete picture of your professional influence.
                </Typography>
              </Box>

              <Grid container spacing={4} sx={{ mb: 6 }}>
                {/* Expert Network */}
                <Grid item xs={12} md={6}>
                  <Card
                    sx={{
                      height: "100%",
                      p: 0,
                      border: `1px solid ${
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.12)"
                          : "rgba(0, 0, 0, 0.12)"
                      }`,
                      background:
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.05)"
                          : "rgba(255, 255, 255, 0.8)",
                      boxShadow:
                        theme.palette.mode === "dark"
                          ? "0 2px 8px rgba(0, 0, 0, 0.3)"
                          : "0 2px 8px rgba(0, 0, 0, 0.08)",
                      transition: "all 0.3s ease-in-out",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow:
                          theme.palette.mode === "dark"
                            ? "0 8px 24px rgba(0, 0, 0, 0.4)"
                            : "0 8px 24px rgba(0, 0, 0, 0.12)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        height: 4,
                        bgcolor: "#9c27b0",
                        borderRadius: "4px 4px 0 0",
                      }}
                    />
                    <CardContent sx={{ p: 4 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          mb: 3,
                        }}
                      >
                        <Box
                          sx={{
                            width: 64,
                            height: 64,
                            borderRadius: 2,
                            bgcolor: "#9c27b0",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "white",
                          }}
                        >
                          <Groups sx={{ fontSize: 36 }} />
                        </Box>
                        <Box>
                          <Typography
                            variant="h5"
                            component="h3"
                            fontWeight={700}
                          >
                            Expert Network
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Access to specialized expertise
                          </Typography>
                        </Box>
                      </Box>
                      <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{ lineHeight: 1.8 }}
                      >
                        Has access to a network of verified
                        experts across industries. When you need specialized
                        knowledge or insights beyond their scope, they can
                        connect you with the right expert from the network.
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Data Integrations */}
                <Grid item xs={12} md={6}>
                  <Card
                    sx={{
                      height: "100%",
                      p: 0,
                      border: `1px solid ${
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.12)"
                          : "rgba(0, 0, 0, 0.12)"
                      }`,
                      background:
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.05)"
                          : "rgba(255, 255, 255, 0.8)",
                      boxShadow:
                        theme.palette.mode === "dark"
                          ? "0 2px 8px rgba(0, 0, 0, 0.3)"
                          : "0 2px 8px rgba(0, 0, 0, 0.08)",
                      transition: "all 0.3s ease-in-out",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow:
                          theme.palette.mode === "dark"
                            ? "0 8px 24px rgba(0, 0, 0, 0.4)"
                            : "0 8px 24px rgba(0, 0, 0, 0.12)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        height: 4,
                        bgcolor: "#1976d2",
                        borderRadius: "4px 4px 0 0",
                      }}
                    />
                    <CardContent sx={{ p: 4 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          mb: 3,
                        }}
                      >
                        <Box
                          sx={{
                            width: 64,
                            height: 64,
                            borderRadius: 2,
                            bgcolor: "#1976d2",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "white",
                          }}
                        >
                          <LinkIcon sx={{ fontSize: 36 }} />
                        </Box>
                        <Box>
                          <Typography
                            variant="h5"
                            component="h3"
                            fontWeight={700}
                          >
                            Data Integrations
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Comprehensive data sources
                          </Typography>
                        </Box>
                      </Box>
                      <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{ lineHeight: 1.8, mb: 3 }}
                      >
                        Connect your data sources to give your Chief of Staff
                        complete context about your relationships, network, and
                        business operations.
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Integration Details */}
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={4}>
                  <Box
                    sx={{
                      p: 3,
                      borderRadius: 2,
                      bgcolor:
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.03)"
                          : "rgba(255, 255, 255, 0.8)",
                      border: `1px solid ${
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.1)"
                          : "rgba(0, 0, 0, 0.1)"
                      }`,
                      height: "100%",
                      transition: "all 0.3s ease-in-out",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: 2,
                        borderColor: "#1976d2",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        mb: 2,
                      }}
                    >
                      <Email sx={{ fontSize: 32, color: "#1976d2" }} />
                      <Typography variant="h6" component="h4" fontWeight={700}>
                        Email archives
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ lineHeight: 1.7 }}
                    >
                      Monitors your email to understand communication patterns,
                      relationship context, and important conversations, keeping
                      you informed of critical updates.
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <Box
                    sx={{
                      p: 3,
                      borderRadius: 2,
                      bgcolor:
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.03)"
                          : "rgba(255, 255, 255, 0.8)",
                      border: `1px solid ${
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.1)"
                          : "rgba(0, 0, 0, 0.1)"
                      }`,
                      height: "100%",
                      transition: "all 0.3s ease-in-out",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: 2,
                        borderColor: "#1976d2",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        mb: 2,
                      }}
                    >
                      <Public sx={{ fontSize: 32, color: "#1976d2" }} />
                      <Typography variant="h6" component="h4" fontWeight={700}>
                        Meeting schedules
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ lineHeight: 1.7 }}
                    >
                      Tracks your meeting history and upcoming appointments to understand relationship frequency and context.
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <Box
                    sx={{
                      p: 3,
                      borderRadius: 2,
                      bgcolor:
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.03)"
                          : "rgba(255, 255, 255, 0.8)",
                      border: `1px solid ${
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.1)"
                          : "rgba(0, 0, 0, 0.1)"
                      }`,
                      height: "100%",
                      transition: "all 0.3s ease-in-out",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: 2,
                        borderColor: "#9c27b0",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        mb: 2,
                      }}
                    >
                      <LinkIcon sx={{ fontSize: 32, color: "#9c27b0" }} />
                      <Typography variant="h6" component="h4" fontWeight={700}>
                        CRM or deal pipelines
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ lineHeight: 1.7 }}
                    >
                      Integrates with your CRM systems to track deals, relationships, and business opportunities.
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <Box
                    sx={{
                      p: 3,
                      borderRadius: 2,
                      bgcolor:
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.03)"
                          : "rgba(255, 255, 255, 0.8)",
                      border: `1px solid ${
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.1)"
                          : "rgba(0, 0, 0, 0.1)"
                      }`,
                      height: "100%",
                      transition: "all 0.3s ease-in-out",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: 2,
                        borderColor: "#2e7d32",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        mb: 2,
                      }}
                    >
                      <Storage sx={{ fontSize: 32, color: "#2e7d32" }} />
                      <Typography variant="h6" component="h4" fontWeight={700}>
                        Notes and institutional memory
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ lineHeight: 1.7 }}
                    >
                      Captures your personal notes, meeting summaries, and institutional knowledge to build comprehensive context.
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <Box
                    sx={{
                      p: 3,
                      borderRadius: 2,
                      bgcolor:
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.03)"
                          : "rgba(255, 255, 255, 0.8)",
                      border: `1px solid ${
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.1)"
                          : "rgba(0, 0, 0, 0.1)"
                      }`,
                      height: "100%",
                      transition: "all 0.3s ease-in-out",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: 2,
                        borderColor: "#f57c00",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        mb: 2,
                      }}
                    >
                      <Public sx={{ fontSize: 32, color: "#f57c00" }} />
                      <Typography variant="h6" component="h4" fontWeight={700}>
                        Social media signals
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ lineHeight: 1.7 }}
                    >
                      Scans social media activity, tracks mentions, and keeps you informed about what your network is sharing and discussing.
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <Box
                    sx={{
                      p: 3,
                      borderRadius: 2,
                      bgcolor:
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.03)"
                          : "rgba(255, 255, 255, 0.8)",
                      border: `1px solid ${
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.1)"
                          : "rgba(0, 0, 0, 0.1)"
                      }`,
                      height: "100%",
                      transition: "all 0.3s ease-in-out",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: 2,
                        borderColor: "#0288d1",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        mb: 2,
                      }}
                    >
                      <Cloud sx={{ fontSize: 32, color: "#0288d1" }} />
                      <Typography variant="h6" component="h4" fontWeight={700}>
                        Private files and research
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ lineHeight: 1.7 }}
                    >
                      Access your private files, research documents, and personal knowledge base for complete context.
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
              <Box sx={{ textAlign: "center", mt: 4 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                  Always private. Always yours.
                </Typography>
              </Box>
            </Box>
          </Fade>

          {/* Subtle Divider */}
          <Box sx={{ mb: 8, display: "flex", justifyContent: "center" }}>
            <Divider
              sx={{
                width: "100px",
                borderColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.12)"
                    : "rgba(0, 0, 0, 0.12)",
                borderWidth: 1,
              }}
            />
          </Box>

          {/* Expert Support Section */}
          <Fade in={fadeIn} timeout={2500}>
            <Box sx={{ mb: 12 }}>
              <Box sx={{ textAlign: "center", mb: 6 }}>
                <Typography
                  variant="h3"
                  component="h2"
                  sx={{
                    mb: 2,
                    fontWeight: 700,
                  }}
                >
                  Expert Support When You Need It
                </Typography>
                <Typography
                  variant="h6"
                  component="p"
                  sx={{
                    color: "text.secondary",
                    maxWidth: "800px",
                    mx: "auto",
                    fontSize: "1.1rem",
                    lineHeight: 1.7,
                  }}
                >
                  Your Chief of Staff can connect you with specialized professionals who support your goals.
                </Typography>
              </Box>

              <Grid container spacing={4}>
                <Grid item xs={12} sm={6} md={4}>
                  <Card
                    sx={{
                      height: "100%",
                      p: 0,
                      border: `1px solid ${
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.12)"
                          : "rgba(0, 0, 0, 0.12)"
                      }`,
                      background:
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.05)"
                          : "rgba(255, 255, 255, 0.8)",
                      boxShadow:
                        theme.palette.mode === "dark"
                          ? "0 2px 8px rgba(0, 0, 0, 0.3)"
                          : "0 2px 8px rgba(0, 0, 0, 0.08)",
                      transition: "all 0.3s ease-in-out",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow:
                          theme.palette.mode === "dark"
                            ? "0 8px 24px rgba(0, 0, 0, 0.4)"
                            : "0 8px 24px rgba(0, 0, 0, 0.12)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        height: 4,
                        bgcolor: "#1976d2",
                        borderRadius: "4px 4px 0 0",
                      }}
                    />
                    <CardContent sx={{ p: 4 }}>
                      <Typography
                        variant="h5"
                        component="h3"
                        fontWeight={700}
                        sx={{ mb: 2 }}
                      >
                        Investor relations strategists
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ lineHeight: 1.7 }}
                      >
                        Specialists in building and maintaining relationships with investors, LPs, and stakeholders.
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <Card
                    sx={{
                      height: "100%",
                      p: 0,
                      border: `1px solid ${
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.12)"
                          : "rgba(0, 0, 0, 0.12)"
                      }`,
                      background:
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.05)"
                          : "rgba(255, 255, 255, 0.8)",
                      boxShadow:
                        theme.palette.mode === "dark"
                          ? "0 2px 8px rgba(0, 0, 0, 0.3)"
                          : "0 2px 8px rgba(0, 0, 0, 0.08)",
                      transition: "all 0.3s ease-in-out",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow:
                          theme.palette.mode === "dark"
                            ? "0 8px 24px rgba(0, 0, 0, 0.4)"
                            : "0 8px 24px rgba(0, 0, 0, 0.12)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        height: 4,
                        bgcolor: "#9c27b0",
                        borderRadius: "4px 4px 0 0",
                      }}
                    />
                    <CardContent sx={{ p: 4 }}>
                      <Typography
                        variant="h5"
                        component="h3"
                        fontWeight={700}
                        sx={{ mb: 2 }}
                      >
                        Analysts for deal or LP research
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ lineHeight: 1.7 }}
                      >
                        Expert analysts providing deep research on deals, limited partners, and investment opportunities.
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <Card
                    sx={{
                      height: "100%",
                      p: 0,
                      border: `1px solid ${
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.12)"
                          : "rgba(0, 0, 0, 0.12)"
                      }`,
                      background:
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.05)"
                          : "rgba(255, 255, 255, 0.8)",
                      boxShadow:
                        theme.palette.mode === "dark"
                          ? "0 2px 8px rgba(0, 0, 0, 0.3)"
                          : "0 2px 8px rgba(0, 0, 0, 0.08)",
                      transition: "all 0.3s ease-in-out",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow:
                          theme.palette.mode === "dark"
                            ? "0 8px 24px rgba(0, 0, 0, 0.4)"
                            : "0 8px 24px rgba(0, 0, 0, 0.12)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        height: 4,
                        bgcolor: "#2e7d32",
                        borderRadius: "4px 4px 0 0",
                      }}
                    />
                    <CardContent sx={{ p: 4 }}>
                      <Typography
                        variant="h5"
                        component="h3"
                        fontWeight={700}
                        sx={{ mb: 2 }}
                      >
                        Executive communications experts
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ lineHeight: 1.7 }}
                      >
                        Professionals specializing in executive messaging, positioning, and strategic communication.
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <Card
                    sx={{
                      height: "100%",
                      p: 0,
                      border: `1px solid ${
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.12)"
                          : "rgba(0, 0, 0, 0.12)"
                      }`,
                      background:
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.05)"
                          : "rgba(255, 255, 255, 0.8)",
                      boxShadow:
                        theme.palette.mode === "dark"
                          ? "0 2px 8px rgba(0, 0, 0, 0.3)"
                          : "0 2px 8px rgba(0, 0, 0, 0.08)",
                      transition: "all 0.3s ease-in-out",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow:
                          theme.palette.mode === "dark"
                            ? "0 8px 24px rgba(0, 0, 0, 0.4)"
                            : "0 8px 24px rgba(0, 0, 0, 0.12)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        height: 4,
                        bgcolor: "#f57c00",
                        borderRadius: "4px 4px 0 0",
                      }}
                    />
                    <CardContent sx={{ p: 4 }}>
                      <Typography
                        variant="h5"
                        component="h3"
                        fontWeight={700}
                        sx={{ mb: 2 }}
                      >
                        Data analysts
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ lineHeight: 1.7 }}
                      >
                        Data specialists skilled in statistical analysis, business intelligence, and data visualization.
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <Card
                    sx={{
                      height: "100%",
                      p: 0,
                      border: `1px solid ${
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.12)"
                          : "rgba(0, 0, 0, 0.12)"
                      }`,
                      background:
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.05)"
                          : "rgba(255, 255, 255, 0.8)",
                      boxShadow:
                        theme.palette.mode === "dark"
                          ? "0 2px 8px rgba(0, 0, 0, 0.3)"
                          : "0 2px 8px rgba(0, 0, 0, 0.08)",
                      transition: "all 0.3s ease-in-out",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow:
                          theme.palette.mode === "dark"
                            ? "0 8px 24px rgba(0, 0, 0, 0.4)"
                            : "0 8px 24px rgba(0, 0, 0, 0.12)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        height: 4,
                        bgcolor: "#0288d1",
                        borderRadius: "4px 4px 0 0",
                      }}
                    />
                    <CardContent sx={{ p: 4 }}>
                      <Typography
                        variant="h5"
                        component="h3"
                        fontWeight={700}
                        sx={{ mb: 2 }}
                      >
                        Designers for pitch decks or promotion materials
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ lineHeight: 1.7 }}
                      >
                        Expert designers specializing in pitch decks, presentations, and promotional materials.
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <Card
                    sx={{
                      height: "100%",
                      p: 0,
                      border: `1px solid ${
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.12)"
                          : "rgba(0, 0, 0, 0.12)"
                      }`,
                      background:
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.05)"
                          : "rgba(255, 255, 255, 0.8)",
                      boxShadow:
                        theme.palette.mode === "dark"
                          ? "0 2px 8px rgba(0, 0, 0, 0.3)"
                          : "0 2px 8px rgba(0, 0, 0, 0.08)",
                      transition: "all 0.3s ease-in-out",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow:
                          theme.palette.mode === "dark"
                            ? "0 8px 24px rgba(0, 0, 0, 0.4)"
                            : "0 8px 24px rgba(0, 0, 0, 0.12)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        height: 4,
                        bgcolor: "#d32f2f",
                        borderRadius: "4px 4px 0 0",
                      }}
                    />
                    <CardContent sx={{ p: 4 }}>
                      <Typography
                        variant="h5"
                        component="h3"
                        fontWeight={700}
                        sx={{ mb: 2 }}
                      >
                        Researchers for industry, policy, or competitive intelligence
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ lineHeight: 1.7 }}
                      >
                        Expert researchers providing deep insights into industry trends, policy changes, and competitive landscapes.
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
              <Box sx={{ textAlign: "center", mt: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  You get the support of a high-end team, without hiring one.
                </Typography>
              </Box>
            </Box>
          </Fade>
        </Container>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          borderTop: 1,
          borderColor: "divider",
          py: 4,
          bgcolor: "background.paper",
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              © {new Date().getFullYear()} MyCatalyze. All rights reserved.
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};
